import Cookies from 'js-cookie';
import UserRole from '../chat/enums/UserRole';
import { getWorkerAuthToken, isBrowserEnv } from '../worker/env';
// import { logger } from "../utils/Logger";

export type AuthInfo = {
  uuid?: string;
  role: UserRole;
};

export const defaultAuthInfo = (): AuthInfo => {
  return {
    role: UserRole.UNKNOWN
  };
};

export const getAuthUserInfoOrDefault = (): AuthInfo => {
  try {
    return getAuthUserInfo();
  } catch {
    return defaultAuthInfo();
  }
};

export const getAuthUserInfo = (): AuthInfo => {
  const authToken = getAuthToken();

  if (!authToken) {
    // logger.error("auth info missing")
    throw new Error('auth info missing');
  }

  const parsed = JSON.parse(atob(authToken.split('.')[1]));

  const authInfo = defaultAuthInfo();

  if ('student_uuid' in parsed) {
    authInfo.uuid = parsed.student_uuid;
    authInfo.role = UserRole.STUDENT;
  } else if ('cms_user_uuid' in parsed) {
    authInfo.uuid = parsed.cms_user_uuid;
    authInfo.role = UserRole.CARE;
  } else if ('teacher_uuid' in parsed) {
    authInfo.uuid = parsed.teacher_uuid;
    authInfo.role = UserRole.TEACHER;
  } else if ('user_identity' in parsed) {
    authInfo.uuid = parsed.user_identity;
    authInfo.role = parsed.role.toLowerCase();
  } else {
    // logger.error("token parsing failed")
    throw 'token parsing failed';
  }

  return authInfo;
};

export const getCookie = (cookie_name: string) => Cookies.get(cookie_name);

export const removeCookie = (cookie_name: string) => {
  Cookies.remove(cookie_name, { path: '/', domain: '.myyogateacher.com' });
};

export const setCookie = (cookie_name: string, value: string, age = 5184000) => {
  try {
    Cookies.set(cookie_name, value, {
      expires: age,
      path: '/',
      domain: '.myyogateacher.com',
      secure: true,
      sameSite: 'Strict'
    });
  } catch {
    // logger.debug(e);
  }
};

export const loadCookie = () => {
  if (!isBrowserEnv()) {
    throw 'loadCookie not available in worker context';
  }
  const hostname = window.location.hostname;

  if (hostname.startsWith('teacher')) {
    const cookie = getCookie('teacher_token_1');
    if (!cookie) throw 'Teacher auth cookie not present';
    return cookie;
  }

  if (hostname.startsWith('hridaya')) {
    const cookie = getCookie('care_chat_token');
    if (!cookie) throw 'Care auth cookie not present';
    return cookie;
  }

  const studentCookie = getCookie('student_token');
  const impersonationCookie = getCookie('impersonationFromTeacherToken');
  if (!studentCookie && !impersonationCookie) throw 'Student auth cookie not present';
  return studentCookie || impersonationCookie;
};

export const getAuthToken = (): string => {
  if (import.meta.env.SSR) {
    throw 'not authenticated';
  }

  if (!isBrowserEnv()) {
    const token = getWorkerAuthToken();
    if (!token) {
      throw 'No auth token provided for worker';
    }
    return token;
  }

  const hostname = window.location.hostname;

  if (!hostname.endsWith('myyogateacher.com')) {
    if (!hostname) {
      throw 'Cookie not found in webview';
    }
    throw 'hostname not supported';
  }

  try {
    const cookie = loadCookie();
    if (!cookie) {
      throw 'No auth cookie found';
    }
    return cookie;
  } catch (error) {
    throw error;
  }
};
